import { Component, DestroyRef, HostListener, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, forkJoin } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  csvBool,
  csvField,
  detectDelimiter,
  tokenizeCsv,
} from '../../../utils/csv.util';
import { ConfirmDialogComponent } from '../../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { FaqDialogComponent } from '../../../components/dialogs/faq-dialog/faq-dialog.component';
import { FileUploadDialogComponent } from '../../../components/dialogs/file-upload-dialog/file-upload-dialog.component';
import { CloudSyncDialogComponent } from '../../../components/dialogs/cloud-sync-dialog/cloud-sync-dialog.component';
import { DozentFaq, DozentService } from '../../../services/dozent.service';
import { TutorService, Tutor } from '../../../services/tutor.service';
import { IFile } from '../../../services/bucket.service';
import { FileUploadQueueService } from '../../../services/file-upload-queue.service';

interface FaqGroup {
  category: string;
  faqs: DozentFaq[];
}

@Component({
  selector: 'app-dozent-faqs',
  templateUrl: './faqs.component.html',
  styleUrl: './faqs.component.scss',
})
export class DozentFaqsComponent implements OnInit {
  activeTab: 'faq' | 'files' = 'faq';

  faqs: DozentFaq[] = [];
  /** All bots — kept for getTutorName compatibility. UI filters use the
   *  `dozents` getter instead, which restricts to alpha_dozent bots. */
  tutors: Tutor[] = [];
  courses: any[] = [];
  isLoading = true;
  searchText = '';
  groupByCategory = false;
  collapsedGroups = new Set<string>();
  faqPage = 0;
  faqPageSize = 25;
  faqPageSizeOptions = [25, 50, 100];

  selectedBotId: string | undefined;
  statusFilter: 'all' | 'active' | 'inactive' = 'all';

  /** True while a CSV import runs (disables the import button). */
  importingFaqs = false;
  /** The FAQ row whose 3-dot action menu is currently open, or null. */
  menuFaq: DozentFaq | null = null;
  /** True while the toolbar actions dropdown (add / template / upload)
   *  is open. */
  toolbarMenuOpen = false;

  /** Bots restricted to alpha_dozent template — drives the filter
   *  dropdown and the dialog's "Dozent zuordnen" select. FAQs are
   *  always attached to a dozent-bot, never to a tutor-bot directly. */
  get dozents(): Tutor[] {
    const dozentTemplateId = this.tutorService.tutorTemplates$.value.find(
      (tpl) => tpl.name === 'alpha_dozent'
    )?.id;
    if (!dozentTemplateId) return [];
    return this.tutors.filter((t) => t.template_id === dozentTemplateId);
  }

  // Files tab
  knowledgeBucketId: string | null = null;
  knowledgeFiles: IFile[] = [];
  isLoadingFiles = false;
  fileSearchText = '';
  filePage = 0;
  filePageSize = 25;
  filePageSizeOptions = [25, 50, 100];

  defaultCategories = [
    'general',
    'course',
    'exam',
    'organization',
    'technical',
    'other',
  ];

  get categories(): string[] {
    const custom = this.faqs
      .map((f) => f.category)
      .filter((c) => c && !this.defaultCategories.includes(c));
    return [...this.defaultCategories, ...new Set(custom)];
  }

  private readonly destroyRef = inject(DestroyRef);
  private readonly uploadQueue = inject(FileUploadQueueService);

  constructor(
    private dozentService: DozentService,
    private tutorService: TutorService,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private t: TranslateService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loadFaqs();
    // Uploads laufen im Hintergrund weiter (FileUploadQueueService).
    this.uploadQueue.batchDone$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ bucketId }) => {
        if (bucketId === this.knowledgeBucketId) this.loadKnowledgeFiles();
      });
    this.tutorService.listTutors().subscribe();
    // Templates are needed so `dozents` getter can resolve the
    // alpha_dozent template id and filter the tutor list.
    if (this.tutorService.tutorTemplates$.value.length === 0) {
      this.tutorService.listTutorTemplates().subscribe();
    }
    this.tutorService.tutors$.subscribe((t) => (this.tutors = t));
    this.dozentService.listCourses().subscribe((c) => (this.courses = c));

    // Handle deep-link from learning_suggestion notification
    this.route.queryParams.subscribe((params) => {
      if (params['suggestionId']) {
        this.loadSuggestionAndOpenDialog(params['suggestionId']);
      }
    });
  }

  private loadSuggestionAndOpenDialog(suggestionId: string) {
    this.dozentService.listLearningSuggestions('pending').subscribe({
      next: (suggestions: any[]) => {
        const suggestion = suggestions.find((s: any) => s.id === suggestionId);
        if (suggestion) {
          // Pre-seed assignment from the active filter (if any); the
          // user can still toggle others in the multi-select.
          const seed = this.selectedBotId ? [this.selectedBotId] : [];
          this.openFaqDialog({
            question: suggestion.suggested_question,
            answer: suggestion.suggested_answer,
            category: suggestion.suggested_category || 'general',
            is_active: true,
            dozent_bot_ids: seed,
          } as any);
        }
      },
    });
  }

  loadFaqs() {
    this.isLoading = true;
    this.dozentService.listFaqs(this.selectedBotId).subscribe({
      next: (faqs) => {
        this.faqs = faqs;
        this.clampFaqPage();
        this.isLoading = false;
      },
      error: () => (this.isLoading = false),
    });
  }

  onDozentScopeChange(): void {
    this.faqPage = 0;
    this.loadFaqs();
  }

  onBotFilterChange(botId: string) {
    this.selectedBotId = botId;
    this.faqPage = 0;
    this.loadFaqs();
  }

  get filteredFaqs(): DozentFaq[] {
    let result = this.faqs;
    if (this.statusFilter === 'active') {
      result = result.filter((f) => f.is_active);
    } else if (this.statusFilter === 'inactive') {
      result = result.filter((f) => !f.is_active);
    }
    if (this.searchText) {
      const s = this.searchText.toLowerCase();
      result = result.filter(
        (f) =>
          f.question.toLowerCase().includes(s) ||
          f.answer.toLowerCase().includes(s) ||
          f.category.toLowerCase().includes(s)
      );
    }
    return result;
  }

  get pagedFaqs(): DozentFaq[] {
    const rows = this.filteredFaqs;
    const maxPage = Math.max(Math.ceil(rows.length / this.faqPageSize) - 1, 0);
    const page = Math.min(this.faqPage, maxPage);
    const start = page * this.faqPageSize;
    return rows.slice(start, start + this.faqPageSize);
  }

  get groupedFaqs(): FaqGroup[] {
    const map = new Map<string, DozentFaq[]>();
    for (const cat of this.categories) {
      const items = this.pagedFaqs.filter((f) => f.category === cat);
      if (items.length > 0) {
        map.set(cat, items);
      }
    }
    // Catch any uncategorized
    const uncategorized = this.pagedFaqs.filter(
      (f) => !this.categories.includes(f.category)
    );
    if (uncategorized.length > 0) {
      map.set('other', [...(map.get('other') || []), ...uncategorized]);
    }
    return Array.from(map.entries()).map(([category, faqs]) => ({
      category,
      faqs,
    }));
  }

  toggleGroup(category: string) {
    if (this.collapsedGroups.has(category)) {
      this.collapsedGroups.delete(category);
    } else {
      this.collapsedGroups.add(category);
    }
  }

  onFaqSearchChange(): void {
    this.faqPage = 0;
  }

  onStatusFilterChange(status: string): void {
    if (status !== 'all' && status !== 'active' && status !== 'inactive') return;
    this.statusFilter = status;
    this.faqPage = 0;
  }

  onGroupByCategoryChange(grouped: unknown): void {
    this.groupByCategory = !!grouped;
    this.faqPage = 0;
  }

  onFaqPageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.filteredFaqs.length / this.faqPageSize) - 1, 0);
    this.faqPage = Math.max(0, Math.min(page, maxPage));
  }

  onFaqPageSizeChange(size: number): void {
    this.faqPageSize = Math.min(Math.max(size, 1), 100);
    this.faqPage = 0;
  }

  private clampFaqPage(): void {
    const maxPage = Math.max(Math.ceil(this.filteredFaqs.length / this.faqPageSize) - 1, 0);
    this.faqPage = Math.max(0, Math.min(this.faqPage, maxPage));
  }

  // ── Dialog ──

  /**
   * Guarantees `tutors$` AND `tutorTemplates$` are populated before the
   * caller continues. Without this, the FAQ dialog could open with an
   * empty dozent multi-select even though the DB has bots — both
   * sources are async, the `dozents` getter combines them snapshot-
   * wise, and the dialog receives that snapshot once at open-time
   * (no further updates flow in). Returns immediately if both caches
   * already hold data, otherwise re-fetches the missing one(s) and
   * waits for completion.
   */
  private ensureDozentDataLoaded(): Observable<void> {
    const needTutors = !this.tutors || this.tutors.length === 0;
    const needTemplates =
      this.tutorService.tutorTemplates$.value.length === 0;
    if (!needTutors && !needTemplates) return of(void 0);

    const calls: Observable<unknown>[] = [];
    if (needTutors) calls.push(this.tutorService.listTutors());
    if (needTemplates) calls.push(this.tutorService.listTutorTemplates());
    return forkJoin(calls).pipe(switchMap(() => of(void 0)));
  }

  openFaqDialog(faq?: DozentFaq) {
    this.ensureDozentDataLoaded().subscribe(() =>
      this._openFaqDialogNow(faq),
    );
  }

  private _openFaqDialogNow(faq?: DozentFaq) {
    this.dialogService.open(FaqDialogComponent, {
      context: {
        faq: faq
          ? { ...faq }
          : {
              question: '',
              answer: '',
              category: 'general',
              is_active: true,
              // Pre-seed the multi-select with the active filter value
              // so the dozent admin doesn't have to re-pick when they
              // were already filtered to a specific dozent.
              dozent_bot_ids: this.selectedBotId ? [this.selectedBotId] : [],
            },
        // FAQs map to dozent-bots only — restrict the dialog's picker
        // to that subset. The `dozents` getter is evaluated AFTER
        // `ensureDozentDataLoaded` resolved, so the snapshot now
        // contains every alpha_dozent bot the current user is allowed
        // to see (admin → all, dozent → own per UserBotAccess).
        tutors: this.dozents,
        courses: this.courses,
        categories: this.categories,
        onConfirm: (model: Partial<DozentFaq>) => {
          // Discriminate edit vs. create on the actual id, not on
          // the truthiness of `faq`. The suggestion-acceptance flow
          // hands in a preseed object (question/answer/category)
          // *without* an id — without this guard those calls landed
          // on `PUT /faqs/undefined` instead of `POST /faqs`.
          const isEdit = !!faq?.id;
          const obs$ = isEdit
            ? this.dozentService.updateFaq(faq!.id, model)
            : this.dozentService.createFaq(model);

          obs$.subscribe({
            next: () => {
              this.toastrService.success(
                this.t.instant(
                  isEdit
                    ? 'pages.dozent.faqs.updated'
                    : 'pages.dozent.faqs.created'
                ),
                ''
              );
              this.loadFaqs();
            },
            error: () => {
              this.toastrService.danger(this.t.instant('toasts.error'), '');
            },
          });
        },
      },
    });
  }

  onDeleteFaq(faq: DozentFaq) {
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        onConfirm: () => {
          this.dozentService.deleteFaq(faq.id).subscribe({
            next: () => {
              this.toastrService.success(
                this.t.instant('pages.dozent.faqs.deleted'),
                ''
              );
              this.loadFaqs();
              ref.close();
            },
          });
        },
      },
    });
  }

  onToggleActive(faq: DozentFaq) {
    this.dozentService
      .updateFaq(faq.id, { is_active: !faq.is_active })
      .subscribe({
        next: () => this.loadFaqs(),
      });
  }

  // ── Row action (3-dot) menu ──

  toggleFaqMenu(event: MouseEvent, faq: DozentFaq): void {
    event.stopPropagation();
    this.menuFaq = this.menuFaq === faq ? null : faq;
  }

  isFaqMenuOpen(faq: DozentFaq): boolean {
    return this.menuFaq === faq;
  }

  closeFaqMenu(): void {
    this.menuFaq = null;
  }

  toggleToolbarMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.toolbarMenuOpen = !this.toolbarMenuOpen;
  }

  /** Close every open dropdown when clicking anywhere else. */
  @HostListener('document:click')
  onDocumentClick(): void {
    this.menuFaq = null;
    this.toolbarMenuOpen = false;
  }

  // ── CSV template + import ──

  private static readonly CSV_HEADERS = ['Frage', 'Antwort', 'Kategorie', 'Aktiv'];

  /** German category label → stored English key. Unknown values pass
   *  through unchanged (custom categories are allowed). */
  private mapCategory(raw: string | undefined): string {
    const v = (raw || '').trim().toLowerCase();
    if (!v) return 'general';
    const map: Record<string, string> = {
      allgemein: 'general',
      general: 'general',
      kurs: 'course',
      course: 'course',
      'prüfung': 'exam',
      pruefung: 'exam',
      exam: 'exam',
      organisation: 'organization',
      organization: 'organization',
      technik: 'technical',
      technical: 'technical',
      sonstiges: 'other',
      other: 'other',
    };
    return map[v] ?? v;
  }

  downloadFaqTemplate(): void {
    const header = DozentFaqsComponent.CSV_HEADERS.join(',');
    const example = [
      csvField('Wann ist die nächste Prüfung?'),
      csvField('Die nächste Prüfung ist am 27.07.2026 um 09:00 Uhr.'),
      csvField('Prüfung'),
      csvField('ja'),
    ].join(',');
    const csv = '﻿' + header + '\r\n' + example + '\r\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'faq-wissen-vorlage.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  onFaqCsvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    // Every dozent FAQ must be attached to at least one dozent-bot. The
    // CSV has no bot column, so we assign imported rows to the dozent
    // currently selected in the filter — refuse otherwise.
    if (!this.selectedBotId) {
      this.toastrService.warning(
        this.t.instant('pages.dozent.faqs.importNoDozent'),
        this.t.instant('toasts.error'),
        { duration: 6000 },
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = (reader.result as string) ?? '';
      let entries: Partial<DozentFaq>[];
      try {
        entries = this.parseFaqCsv(text);
      } catch (err) {
        console.error('[dozent-faqs] csv parse failed', err);
        this.toastrService.danger(
          this.t.instant('pages.dozent.faqs.importError'),
          this.t.instant('toasts.error'),
        );
        return;
      }
      if (!entries.length) {
        this.toastrService.warning(
          this.t.instant('pages.dozent.faqs.importEmpty'),
          this.t.instant('toasts.error'),
        );
        return;
      }
      this.bulkCreateFaqs(entries);
    };
    reader.onerror = () =>
      this.toastrService.danger(
        this.t.instant('pages.dozent.faqs.importError'),
        this.t.instant('toasts.error'),
      );
    reader.readAsText(file, 'utf-8');
  }

  private bulkCreateFaqs(entries: Partial<DozentFaq>[]): void {
    const botId = this.selectedBotId;
    if (!botId) return;
    this.importingFaqs = true;
    const base = this.faqs.length;
    const calls = entries.map((e, i) =>
      this.dozentService.createFaq({
        ...e,
        dozent_bot_ids: [botId],
        version: '1.0',
        sort_order: base + i,
      }),
    );
    forkJoin(calls).subscribe({
      next: () => {
        this.importingFaqs = false;
        this.toastrService.success(
          this.t.instant('pages.dozent.faqs.importSuccess', {
            count: entries.length,
          }),
          '',
        );
        this.loadFaqs();
      },
      error: (err) => {
        this.importingFaqs = false;
        console.error('[dozent-faqs] bulk import failed', err);
        this.toastrService.danger(
          this.t.instant('pages.dozent.faqs.importError'),
          this.t.instant('toasts.error'),
          { duration: 6000 },
        );
        this.loadFaqs();
      },
    });
  }

  /** Parse a FAQ CSV → create payloads. Maps columns by header name
   *  (DE + EN aliases); rows missing question or answer are skipped. */
  private parseFaqCsv(text: string): Partial<DozentFaq>[] {
    const stripped = text.replace(/^﻿/, '');
    const firstLine = stripped.split(/\r?\n/, 1)[0] ?? '';
    const delimiter = detectDelimiter(firstLine);
    const table = tokenizeCsv(stripped, delimiter);
    if (!table.length) return [];

    const firstRow = table[0].map((c) => c.trim().toLowerCase());
    const hasHeader = firstRow.some(
      (c) =>
        this.mapFaqHeader(c) === 'question' ||
        this.mapFaqHeader(c) === 'answer',
    );

    let colMap: Record<string, number>;
    let dataRows: string[][];
    if (hasHeader) {
      colMap = {};
      firstRow.forEach((c, i) => {
        const key = this.mapFaqHeader(c);
        if (key && !(key in colMap)) colMap[key] = i;
      });
      dataRows = table.slice(1);
    } else {
      colMap = { question: 0, answer: 1, category: 2, active: 3 };
      dataRows = table;
    }

    const qIdx = colMap['question'];
    const aIdx = colMap['answer'];
    if (qIdx === undefined || aIdx === undefined) {
      throw new Error('missing question/answer columns');
    }

    const out: Partial<DozentFaq>[] = [];
    for (const row of dataRows) {
      if (row.every((c) => !c || !c.trim())) continue;
      const question = (row[qIdx] ?? '').trim();
      const answer = (row[aIdx] ?? '').trim();
      if (!question || !answer) continue;
      out.push({
        question,
        answer,
        category: this.mapCategory(
          colMap['category'] !== undefined ? row[colMap['category']] : '',
        ),
        is_active:
          colMap['active'] !== undefined
            ? csvBool(row[colMap['active']])
            : true,
      });
    }
    return out;
  }

  private mapFaqHeader(cell: string): string | undefined {
    const c = cell.trim().toLowerCase();
    if (['frage', 'question', 'thema', 'topic'].includes(c)) return 'question';
    if (['antwort', 'answer', 'wissen'].includes(c)) return 'answer';
    if (['kategorie', 'category'].includes(c)) return 'category';
    if (['aktiv', 'active', 'status'].includes(c)) return 'active';
    return undefined;
  }

  getCategoryLabel(category: string): string {
    return this.t.instant(`pages.dozent.faqs.categories.${category}`) || category;
  }

  /** Resolve the list of assigned dozent display names for a FAQ row. */
  getDozentNames(faq: DozentFaq): string[] {
    const ids = faq.dozent_bot_ids ?? [];
    return ids
      .map((id) => this.tutors.find((t) => t.id === id))
      .filter((t): t is Tutor => !!t)
      .map((t) => t.visual_name || t.name);
  }

  // ── Files tab ──

  onTabChange(tabTitle: string) {
    if (tabTitle === this.t.instant('pages.dozent.faqs.tabFiles')) {
      this.activeTab = 'files';
      if (!this.knowledgeBucketId) {
        this.ensureKnowledgeBucket();
      }
    } else {
      this.activeTab = 'faq';
    }
  }

  ensureKnowledgeBucket() {
    this.isLoadingFiles = true;
    this.dozentService.getOrCreateKnowledgeBucket().subscribe({
      next: (bucket: any) => {
        this.knowledgeBucketId = bucket.id;
        this.loadKnowledgeFiles();
      },
      error: () => {
        this.isLoadingFiles = false;
      },
    });
  }

  loadKnowledgeFiles() {
    if (!this.knowledgeBucketId) return;
    this.isLoadingFiles = true;
    this.dozentService.listKnowledgeFiles(this.knowledgeBucketId).subscribe({
      next: (files: IFile[]) => {
        this.knowledgeFiles = files;
        this.clampFilePage();
        this.isLoadingFiles = false;
      },
      error: () => {
        this.isLoadingFiles = false;
      },
    });
  }

  get filteredFiles(): IFile[] {
    if (!this.fileSearchText) return this.knowledgeFiles;
    const q = this.fileSearchText.toLowerCase();
    return this.knowledgeFiles.filter((f) =>
      f.file_name.toLowerCase().includes(q)
    );
  }

  get pagedFiles(): IFile[] {
    const rows = this.filteredFiles;
    const maxPage = Math.max(Math.ceil(rows.length / this.filePageSize) - 1, 0);
    const page = Math.min(this.filePage, maxPage);
    const start = page * this.filePageSize;
    return rows.slice(start, start + this.filePageSize);
  }

  onFileSearchChange(): void {
    this.filePage = 0;
  }

  onFilePageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.filteredFiles.length / this.filePageSize) - 1, 0);
    this.filePage = Math.max(0, Math.min(page, maxPage));
  }

  onFilePageSizeChange(size: number): void {
    this.filePageSize = Math.min(Math.max(size, 1), 100);
    this.filePage = 0;
  }

  private clampFilePage(): void {
    const maxPage = Math.max(Math.ceil(this.filteredFiles.length / this.filePageSize) - 1, 0);
    this.filePage = Math.max(0, Math.min(this.filePage, maxPage));
  }

  openUploadDialog() {
    if (!this.knowledgeBucketId) return;
    this.dialogService.open(FileUploadDialogComponent, {
      context: { bucketId: this.knowledgeBucketId },
    });
  }

  openCloudImportDialog() {
    if (!this.knowledgeBucketId) return;
    const ref = this.dialogService.open(CloudSyncDialogComponent, {
      context: { bucketId: this.knowledgeBucketId },
    });
    ref.onClose.subscribe((result) => {
      if (result) this.loadKnowledgeFiles();
    });
  }

  onDeleteFile(file: IFile) {
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        onConfirm: () => {
          this.dozentService.deleteKnowledgeFile(file.id).subscribe({
            next: () => {
              this.knowledgeFiles = this.knowledgeFiles.filter(
                (f) => f.id !== file.id
              );
              this.clampFilePage();
              this.toastrService.success(
                this.t.instant('pages.dozent.faqs.fileDeleted'),
                ''
              );
              ref.close();
            },
            error: () => {
              this.toastrService.danger(this.t.instant('toasts.error'), '');
            },
          });
        },
      },
    });
  }

  getFileStatusClass(file: IFile): string {
    if (file.status_embedding) return 'ready';
    // A recorded reason means the attempt ran and failed — not the same as
    // "still pending", which is what such files used to look like.
    if (file.embedding_error) return 'failed';
    if (file.file_status === 'processing') return 'processing';
    return 'pending';
  }

  getFileStatusLabel(file: IFile): string {
    if (file.status_embedding) return this.t.instant('pages.dozent.faqs.fileReady');
    if (file.embedding_error) return this.t.instant('pages.dozent.faqs.fileFailed');
    if (file.file_status === 'processing') return this.t.instant('pages.dozent.faqs.fileProcessing');
    return this.t.instant('pages.dozent.faqs.filePending');
  }

  /** Full failure reason, shown as the badge tooltip. Empty for healthy files. */
  getFileStatusTooltip(file: IFile): string {
    return file.status_embedding ? '' : (file.embedding_error || '');
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }
}
